import { AppStatType, DataPeriod, Project } from '@prisma/client';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@zond/ui';
import confetti from 'canvas-confetti';
import { BarChart3, Clock, Coffee, Layout, Plus, Rocket, UserCheck, Users, UserX } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { getLogo } from '../../../utils/get-logo';
import { LOCAL_STORAGE_KEYS } from '../../constants';
import { useCurrentProject } from '../../hooks/useCurrentProject';
import { useStore } from '../../store';
import { useGetProjects, useGetProjectStats } from '../projects/projects.queries';
import { useAppStats, useLatestReports } from '../reports/user-report.queries';
import { AbsentUsersCard } from './components/AbsentUsersCard';
import { ActiveUsersCard } from './components/ActiveUsersCard';
import { ApplicationsSection } from './components/ApplicationsSection';
import { AttendanceChart } from './components/AttendanceChart';
import { CardSkeleton } from './components/CardSkeleton';
import { IdleUsersCard } from './components/IdleUsersCard';
import { RecentReportsSection } from './components/RecentReportsSection';
import { UsersCard } from './components/UsersCard';
import { WebsitesSection } from './components/WebsitesSection';
import { CALENDAR_URL, DISABLE_WORKSPACE_ACTIVATION_CHECK } from './constant';

const mockAttendanceData = [
  { date: 'Apr 7', present: 450, absent: 50 },
  { date: 'Apr 15', present: 480, absent: 20 },
  { date: 'Apr 23', present: 460, absent: 40 },
  { date: 'May 1', present: 470, absent: 30 },
  { date: 'May 9', present: 440, absent: 60 },
  { date: 'May 17', present: 490, absent: 10 },
  { date: 'May 26', present: 465, absent: 35 },
  { date: 'Jun 3', present: 475, absent: 25 },
  { date: 'Jun 30', present: 480, absent: 20 },
];

export function AdminDashboard() {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const selectedWorkspaceId = useStore((state) => state.getSelectedWorkspaceId());
  const currentWorkspace = useStore((state) => state.getCurrentWorkspace());
  const { currentProject, setProjectId, projects, isLoading: isLoadingProjects } = useCurrentProject();
  const selectedProjectId = currentProject?.id.toString() || '';

  console.log('Dashboard render:', {
    currentProject,
    selectedProjectId,
    projects,
    isLoadingProjects,
  });

  const { data: projectStats, isLoading: isLoadingStats } = useGetProjectStats(selectedProjectId);
  const { data: latestReports, isLoading: isLoadingReports } = useLatestReports({
    projectId: Number(selectedProjectId),
    enabled: !!selectedProjectId,
  });
  const [applicationsRange, setApplicationsRange] = useState<DataPeriod>(DataPeriod.Daily);
  const [websitesRange, setWebsitesRange] = useState<DataPeriod>(DataPeriod.Daily);
  const { data: applications, isLoading: isLoadingApplications } = useAppStats({
    projectId: Number(selectedProjectId),
    range: applicationsRange,
    type: AppStatType.App,
    enabled: !!selectedProjectId,
  });
  const { data: websites, isLoading: isLoadingWebsites } = useAppStats({
    projectId: Number(selectedProjectId),
    range: websitesRange,
    type: AppStatType.Website,
    enabled: !!selectedProjectId,
  });
  const [lastRefresh, setLastRefresh] = useState<string>(
    new Date().toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  );

  const isLoading =
    isLoadingProjects || isLoadingStats || isLoadingReports || isLoadingApplications || isLoadingWebsites;

  useEffect(() => {
    // Update last refresh time when project stats are loaded
    if (projectStats) {
      setLastRefresh(
        new Date().toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })
      );
    }
  }, [projectStats]);

  // Confetti animation when the sign-up modal is shown
  useEffect(() => {
    if (
      !isLoadingProjects &&
      (!projects || projects.length === 0) &&
      !currentWorkspace?.activated &&
      !DISABLE_WORKSPACE_ACTIVATION_CHECK
    ) {
      confetti({
        particleCount: 120,
        spread: 80,
        origin: { y: 0.4 },
        colors: ['#6366f1', '#a5b4fc', '#fbbf24', '#f472b6', '#34d399'],
      });
    }
  }, [isLoadingProjects, projects, currentWorkspace]);

  const handleProjectChange = (value: string) => {
    console.log('handleProjectChange:', {
      value,
      selectedProjectId,
      currentProject,
    });

    // If project actually changed
    if (value !== selectedProjectId) {
      console.log('Project changing to:', value);

      // Cancel any pending queries for the old project
      queryClient.cancelQueries({ queryKey: ['app-stats'] });
      queryClient.cancelQueries({ queryKey: ['latest-reports'] });
      queryClient.cancelQueries({ queryKey: ['project-stats'] });

      // Remove cached data
      queryClient.removeQueries({ queryKey: ['app-stats'] });
      queryClient.removeQueries({ queryKey: ['latest-reports'] });
      queryClient.removeQueries({ queryKey: ['project-stats'] });

      // Reset states when changing project
      setApplicationsRange(DataPeriod.Daily);
      setWebsitesRange(DataPeriod.Daily);

      // Update selected project ID
      setProjectId(value);
    }
  };

  if (!isLoadingProjects && (!projects || projects.length === 0) && !DISABLE_WORKSPACE_ACTIVATION_CHECK) {
    return (
      <div className="relative h-[calc(100vh-64px)]">
        <div className="absolute inset-0 bg-background/90 dark:bg-background/95 backdrop-blur-xs z-10 flex flex-col items-center justify-center">
          <div className="bg-gradient-to-b from-card to-card rounded-xl p-8 shadow-2xl max-w-xl mx-auto text-center border border-border/50 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex justify-center items-center mb-6">
              <img
                src={getLogo()}
                alt={t('logoAlt')}
                className="w-16 h-16 dark:border dark:border-border/20 dark:rounded-full"
              />
            </div>
            {currentWorkspace?.activated || DISABLE_WORKSPACE_ACTIVATION_CHECK ? (
              <>
                <h2 className="text-3xl font-bold mb-3 bg-gradient-to-r from-primary/90 to-primary bg-clip-text text-transparent">
                  {t('activation.welcomeTitle')}
                </h2>
                <p className="text-muted-foreground text-lg mb-8 max-w-md mx-auto">
                  {t('activation.welcomeDescription')}
                </p>
                <div className="flex items-center justify-center gap-4">
                  <Button
                    size="lg"
                    onClick={() => navigate(`/${selectedWorkspaceId}/projects`)}
                    className="shadow-lg hover:shadow-primary/25 transition-all hover:scale-105 bg-gradient-to-r from-primary to-primary/90"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    {t('activation.createProject')}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-4xl font-extrabold mb-3 bg-gradient-to-r from-primary/90 to-primary bg-clip-text text-transparent drop-shadow-lg">
                  {t('activation.thanksTitle')}
                </h2>
                <p className="text-muted-foreground text-lg mb-4 max-w-md mx-auto">
                  {t('activation.thanksDescription')}
                </p>
                <p className="text-muted-foreground text-lg mb-2 max-w-md mx-auto font-medium">
                  {t('activation.inHurry')}
                </p>
                <p className="text-muted-foreground text-lg mb-8 max-w-md mx-auto">
                  {t('activation.bookDemoPrompt')}
                </p>
                <div className="flex items-center justify-center gap-4">
                  <Button
                    size="lg"
                    variant="default"
                    className="shadow-lg hover:shadow-primary/25 transition-all hover:scale-105 bg-gradient-to-r from-primary to-primary/90 text-lg font-semibold px-8 py-3"
                    asChild
                  >
                    <Link to={CALENDAR_URL} className="flex items-center gap-2">
                      <Rocket className="w-5 h-5" />
                      {t('activation.bookDemo')}
                    </Link>
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="space-y-6 opacity-50 pointer-events-none">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold mb-1">{t('overview.title')}</h2>
              <p className="text-sm text-muted-foreground">{t('overview.lastRefreshEmpty')}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium mb-1">{t('overview.project')}</h3>
              <Select disabled value="placeholder">
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder={t('overview.selectProject')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="placeholder">{t('overview.noProjects')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {isLoading ? (
              <>
                <CardSkeleton />
                <CardSkeleton />
                <CardSkeleton />
                <CardSkeleton />
              </>
            ) : (
              <>
                <UsersCard totalUsers={0} />
                <ActiveUsersCard activeUsers={0} changeText={t('common:states.noData')} />
                <IdleUsersCard idleUsers={0} changeText={t('common:states.noData')} />
                <AbsentUsersCard absentUsers={0} changeText={t('common:states.noData')} />
              </>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <RecentReportsSection reports={[]} isLoading={false} />
            <AttendanceChart isLoading={isLoadingProjects || isLoadingStats} projectId={0} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ApplicationsSection
              applications={[]}
              range={applicationsRange}
              onRangeChange={setApplicationsRange}
              isLoading={false}
            />
            <WebsitesSection
              websites={[]}
              range={DataPeriod.Daily}
              onRangeChange={setWebsitesRange}
              isLoading={false}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col">
      <div className="flex-none p-6 bg-card border-b">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold mb-1">{t('overview.title')}</h2>
            <p className="text-sm text-muted-foreground">
              {t('overview.lastRefresh', { time: lastRefresh })}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div>
              <h3 className="text-sm font-medium mb-1">{t('overview.project')}</h3>
              <Select value={selectedProjectId} onValueChange={handleProjectChange}>
                <SelectTrigger className="w-[200px]" disabled={isLoadingProjects}>
                  <SelectValue placeholder={t('overview.selectProject')} />
                </SelectTrigger>
                <SelectContent>
                  {projects?.map((project) => (
                    <SelectItem key={project.id} value={String(project.id)}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto custom-scrollbar">
        <div className="space-y-6 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {isLoading ? (
              <>
                <CardSkeleton />
                <CardSkeleton />
                <CardSkeleton />
                <CardSkeleton />
              </>
            ) : (
              <>
                <UsersCard totalUsers={projectStats?.stats?.totalMembers || 0} isLoading={isLoading} />
                <ActiveUsersCard
                  activeUsers={projectStats?.stats?.activeUsers || 0}
                  changeText={isLoading ? t('common:states.loading') : t('cards.activeUsers.activeNow')}
                  isLoading={isLoading}
                />
                <IdleUsersCard
                  idleUsers={projectStats?.stats?.idleUsers || 0}
                  changeText={isLoading ? t('common:states.loading') : t('cards.idleUsers.idle')}
                  isLoading={isLoading}
                />
                <AbsentUsersCard
                  absentUsers={projectStats?.stats?.usersWithoutSessionsToday || 0}
                  changeText={isLoading ? t('common:states.loading') : t('cards.absentUsers.noSessions')}
                  isLoading={isLoading}
                />
              </>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <RecentReportsSection reports={latestReports || []} isLoading={isLoadingReports} />
            <AttendanceChart
              isLoading={isLoadingProjects || isLoadingStats}
              projectId={Number(selectedProjectId)}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ApplicationsSection
              applications={applications || []}
              range={applicationsRange}
              onRangeChange={setApplicationsRange}
              isLoading={isLoadingApplications}
            />
            <WebsitesSection
              websites={websites || []}
              range={websitesRange}
              onRangeChange={setWebsitesRange}
              isLoading={isLoadingWebsites}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
